import React from "react";
import { Calendar } from "lucide-react";
import { tone, toneForStatus } from "components/ui/tone";
import { clickable } from "components/ui/interactive";

/**
 * NewTicketCard – displays one support ticket using only API response data
 * API fields: id, date, subject, description, status
 */
export default function NewTicketCard({ ticket, onClick }: { ticket: any; onClick?: (t: any) => void }) {
  const formatDate = (date?: string) => {
    if (!date) return "—";
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Status badge color mapping
  const getStatusColor = (status) =>
  // Delegates to the shared tone system: one status→colour mapping for the
  // whole app, and dark mode comes with it.
  tone(toneForStatus(status)).badge;

  return (
    <article
      className="bg-surface dark:bg-dark-800 rounded-2xl border border-line dark:border-white/[0.07] p-4 hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer"
      {...clickable(() => onClick?.(ticket))}
    >
      {/* Header - ID and Status */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {ticket.id && (
            <span className="font-mono text-xs text-content-muted dark:text-gray-500">#{ticket.id}</span>
          )}
          {ticket.status && (
            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${getStatusColor(ticket.status)}`}>
              {ticket.status}
            </span>
          )}
        </div>
      </div>

      {/* Subject */}
      {ticket.subject && (
        <h3 className="text-sm font-semibold text-content dark:text-white mb-2 line-clamp-2">
          {ticket.subject}
        </h3>
      )}

      {/* Description */}
      {ticket.description && (
        <p className="text-content-muted dark:text-gray-400 text-xs mb-3 line-clamp-2">
          {ticket.description}
        </p>
      )}

      {/* Date */}
      {ticket.date && (
        <div className="flex items-center gap-1 text-xs text-content-subtle dark:text-gray-500">
          <Calendar className="w-3 h-3" />
          <span>{formatDate(ticket.date)}</span>
        </div>
      )}
    </article>
  );
}
